import Pressable from '@/components/motion/pressable';
import type { RunSummary } from '@/entities/sender.entity';
import { cn } from '@/lib/utils';
import SenderStatusChip, { runStatusToChipTone } from '@/pages/sender/components/sender-status-chip';
import { SENDER_RUN_STATUS_LABELS } from '@/pages/sender/sender-run-status-labels';
import {
	formatRunCardMetaLine,
	resolveRunCardStats,
} from '@/pages/sender/sender-run-card-meta';

const STATUS_BORDER: Partial<Record<RunSummary['status'], string>> = {
	running: 'border-l-primary',
	queued: 'border-l-muted-foreground/40',
	completed: 'border-l-chart-2',
	completed_empty: 'border-l-chart-2/60',
	completed_with_warnings: 'border-l-chart-4',
	failed: 'border-l-destructive',
	cancelled: 'border-l-muted-foreground/30',
};

interface SenderRunListItemProps {
	run: RunSummary;
	onClick: () => void;
}

export default function SenderRunListItem({ run, onClick }: SenderRunListItemProps) {
	const stats = resolveRunCardStats(run);
	const progress =
		stats.kind === 'counts' && stats.scheduled > 0
			? Math.min(100, Math.round((stats.delivered / stats.scheduled) * 100))
			: null;

	return (
		<Pressable
			role='button'
			tabIndex={0}
			onClick={onClick}
			className={cn(
				'rounded-xl border border-border border-l-2 bg-card text-left active:bg-accent/30 cursor-pointer',
				STATUS_BORDER[run.status] ?? 'border-l-transparent',
			)}
		>
			<div className='flex flex-col gap-2 px-3 py-3'>
				<div className='flex items-start justify-between gap-2'>
					<p className='min-w-0 flex-1 text-[15px] font-medium leading-snug line-clamp-2'>{run.caption}</p>
					<SenderStatusChip
						label={SENDER_RUN_STATUS_LABELS[run.status] ?? 'Статус'}
						tone={runStatusToChipTone(run.status)}
						className='shrink-0'
					/>
				</div>
				<p className='text-[13px] text-muted-foreground truncate'>{formatRunCardMetaLine(run)}</p>
				{stats.kind === 'counts' ? (
					<div className='flex flex-col gap-1.5'>
						<div className='flex items-baseline justify-between gap-2 text-[13px] tabular-nums'>
							<span className='text-muted-foreground'>Отправлено</span>
							<span className='font-medium'>
								{stats.delivered}
								<span className='text-muted-foreground font-normal'> / {stats.scheduled}</span>
							</span>
						</div>
						{run.status === 'running' && progress != null ? (
							<div className='h-1 overflow-hidden rounded-full bg-muted'>
								<div className='h-full rounded-full bg-primary transition-[width]' style={{ width: `${progress}%` }} />
							</div>
						) : null}
					</div>
				) : stats.kind === 'delivered' ? (
					<p className='text-[13px] tabular-nums text-muted-foreground'>
						Отправлено <span className='font-medium text-foreground'>{stats.delivered}</span>
					</p>
				) : null}
			</div>
		</Pressable>
	);
}
